import { directSupabase } from '../lib/supabaseClient';

export interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  category: string;
  assignee?: string;
  projectId?: string;
  eventType?: string;
  source: 'jarvis' | 'outlook';
  location?: string;
  description?: string;
  color?: string;
}

export interface WorkCategory {
  id: string;
  name: string;
  type: 'internal' | 'subcontractor';
  assignees: string[];
  color: string;
  defaultDuration: number; // minutes
}

// Define work categories based on existing Outlook calendar categories
export const WORK_CATEGORIES: WorkCategory[] = [
  {
    id: 'james',
    name: 'James',
    type: 'internal',
    assignees: ['James'],
    color: '#FF69B4', // Pink
    defaultDuration: 120
  },
  {
    id: 'si-and-james',
    name: 'Si and James',
    type: 'internal',
    assignees: ['Si', 'James'],
    color: '#90EE90', // Light green
    defaultDuration: 180
  },
  {
    id: 'si-and-tom-fitting',
    name: 'Si and Tom Fitting',
    type: 'internal',
    assignees: ['Si', 'Tom'],
    color: '#696969', // Dark grey
    defaultDuration: 240
  },
  {
    id: 'si-fitting',
    name: 'Si Fitting',
    type: 'internal',
    assignees: ['Si'],
    color: '#32CD32', // Green
    defaultDuration: 180
  },
  {
    id: 'si-other',
    name: 'Si Other',
    type: 'internal',
    assignees: ['Si'],
    color: '#D2691E', // Orange/brown
    defaultDuration: 120
  },
  {
    id: 'si-surveying',
    name: 'Si Surveying',
    type: 'internal',
    assignees: ['Si'],
    color: '#4169E1', // Blue
    defaultDuration: 60
  },
  {
    id: 'subby',
    name: 'Subby',
    type: 'subcontractor',
    assignees: ['Subcontractors'],
    color: '#87CEEB', // Light blue
    defaultDuration: 240
  },
  {
    id: 'tom-fitting',
    name: 'Tom Fitting',
    type: 'internal',
    assignees: ['Tom'],
    color: '#9370DB', // Purple
    defaultDuration: 180
  }
];

// Note: Event types and categories are many-to-many and dynamic
// Categories should be assigned per event, not hard-coded by event type
// This mapping is removed as it doesn't reflect the actual business logic

export const calendarService = {
  // Get all calendar events for a date range (read-only)
  async getCalendarEvents(startDate: Date, endDate: Date): Promise<CalendarEvent[]> {
    try {
      // Fetch only the three relevant workflow events from the database
      const { data: projectEvents, error } = await directSupabase
        .from('project_events')
        .select(`
          *,
          project:projects(
            id,
            project_name,
            customer_name,
            customer_email
          )
        `)
        .in('event_type', ['installationDateConfirmation', 'firstSurveyAppointmentConfirmation', 'finalSurveyAppointmentConfirmation'])
        .gte('event_date', startDate.toISOString())
        .lte('event_date', endDate.toISOString())
        .order('event_date', { ascending: true });

      if (error) {
        console.error('Error fetching project events:', error);
        throw error;
      }

      // Transform project events to calendar events
      const calendarEvents: CalendarEvent[] = (projectEvents || []).map(event => {
        // Read category assignment from metadata, fallback to default if not assigned
        const assignedCategory = event.metadata?.category || 'si-and-tom-fitting';
        const categoryConfig = WORK_CATEGORIES.find(cat => cat.id === assignedCategory);
        
        const start = new Date(event.event_date);
        const end = new Date(start.getTime() + (categoryConfig?.defaultDuration || 120) * 60 * 1000);

        return {
          id: event.id,
          title: `${event.project?.customer_name || 'Unknown'} - ${getEventTypeDisplayName(event.event_type)}`,
          start,
          end,
          category: assignedCategory,
          assignee: categoryConfig?.assignees[0], // For now, assign to first available
          projectId: event.project_id,
          eventType: event.event_type,
          source: 'jarvis' as const,
          location: event.project?.customer_email ? `Email: ${event.project.customer_email}` : 'No location specified',
          description: `Project: ${event.project?.project_name}\nEvent: ${getEventTypeDisplayName(event.event_type)}`,
          color: categoryConfig?.color
        };
      });

      return calendarEvents;
    } catch (error) {
      console.error('Error in getCalendarEvents:', error);
      throw error;
    }
  },

  // Get work categories
  async getWorkCategories(): Promise<WorkCategory[]> {
    // For Phase 1, return static categories
    // In future phases, this could fetch from database
    return WORK_CATEGORIES;
  },

  // Get events by category
  async getEventsByCategory(category: string, startDate: Date, endDate: Date): Promise<CalendarEvent[]> {
    const allEvents = await this.getCalendarEvents(startDate, endDate);
    return allEvents.filter(event => event.category === category);
  },

  // Get events by assignee
  async getEventsByAssignee(assignee: string, startDate: Date, endDate: Date): Promise<CalendarEvent[]> {
    const allEvents = await this.getCalendarEvents(startDate, endDate);
    return allEvents.filter(event => event.assignee === assignee);
  },

  // Get upcoming events (next 7 days)
  async getUpcomingEvents(): Promise<CalendarEvent[]> {
    const startDate = new Date();
    const endDate = new Date();
    endDate.setDate(endDate.getDate() + 7);
    
    return this.getCalendarEvents(startDate, endDate);
  },

  // Get today's events
  async getTodayEvents(): Promise<CalendarEvent[]> {
    const today = new Date();
    const startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
    
    return this.getCalendarEvents(startDate, endDate);
  }
};

// Helper function to get display names for event types
function getEventTypeDisplayName(eventType: string): string {
  const displayNames: Record<string, string> = {
    'installationDateConfirmation': 'Installation Confirmation',
    'firstSurveyAppointmentConfirmation': 'First Survey Confirmation',
    'finalSurveyAppointmentConfirmation': 'Final Survey Confirmation'
  };
  
  return displayNames[eventType] || eventType;
} 